import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import AboutSection from '../components/AboutSection';
import HowItWorks from '../components/HowItWorks';
import TrustSection from '../components/TrustSection';
import TestimonialsSection from '../components/TestimonialsSection';
import FAQSection from '../components/FAQSection';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [landingContent, setLandingContent] = useState({
    content: {},
    testimonials: [],
    faqs: [],
  });
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_BASE}/landing/content`, { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          setLandingContent(data);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const floatingVariants = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
          animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-amber-400/10 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{ x: [0, -100, 0], y: [0, 100, 0] }}
          transition={{ duration: 18, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 left-1/4 w-96 h-96 bg-amber-300/5 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{ x: [0, 50, 0], y: [0, -50, 0] }}
          transition={{ duration: 20, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10">
        <motion.nav
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled
              ? 'bg-slate-950/90 backdrop-blur-xl border-b border-amber-500/20 shadow-lg'
              : 'bg-transparent'
          }`}
        >
          <div className="flex justify-between items-center px-8 py-6 max-w-7xl mx-auto w-full">
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent"
            >
              Ceylon Broker
            </motion.h1>

            <div className="hidden md:flex gap-8 items-center">
              <nav className="flex gap-6">
                {['Home', 'How It Works', 'About', 'FAQ'].map((item) => (
                  <motion.a
                    key={item}
                    href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-300 hover:text-amber-300 transition-colors font-medium"
                    whileHover={{ scale: 1.05 }}
                  >
                    {item}
                  </motion.a>
                ))}
              </nav>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/buy')}
                className="px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all duration-300"
              >
                Buy Now
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/admin')}
                className="px-6 py-3 border-2 border-amber-400 text-amber-400 font-semibold rounded-lg hover:bg-amber-400/10 transition-all duration-300"
              >
                Login
              </motion.button>
            </div>

            <motion.button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              whileHover={{ scale: 1.1 }}
            >
              <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </motion.button>
          </div>

          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="md:hidden bg-slate-900/95 border-t border-amber-500/20 px-8 py-6 space-y-4"
            >
              {['Home', 'How It Works', 'About', 'FAQ'].map((item) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block text-gray-300 hover:text-amber-300 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item}
                </motion.a>
              ))}
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate('/buy')}
                className="w-full px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold rounded-lg"
              >
                Buy Now
              </motion.button>
            </motion.div>
          )}
        </motion.nav>

        <motion.div
          className="max-w-7xl mx-auto px-8 py-32 md:py-40 text-center pt-32"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          id="home"
        >
          <motion.h2
            variants={itemVariants}
            className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Buy Digital currency
            <br />
            <span className="bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text text-transparent">
              Made Simple & Trusted
            </span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
              Purchase Bitcoin, Ethereum, and USDT with ease. Fast transactions, transparent pricing,
            and 24/7 support.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-6 justify-center mb-16"
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(251, 191, 36, 0.3)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/buy')}
              className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold text-lg rounded-xl hover:from-amber-300 hover:to-amber-400 transition-all duration-300"
            >
              Start Buying Now
            </motion.button>
            <motion.a
              href="#how-it-works"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border-2 border-amber-400 text-amber-400 font-bold text-lg rounded-xl hover:bg-amber-400/10 transition-all duration-300 inline-flex items-center justify-center"
            >
              Learn More
            </motion.a>
          </motion.div>

          <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-8 mt-20">
            {[
              { icon: '⚡', title: 'Fast Transactions', desc: 'Get your digital currency in minutes' },
              { icon: '🔒', title: 'Secure & Verified', desc: 'Bank-level security standards' },
              { icon: '💰', title: 'Best Rates', desc: '10% transparent commission' },
            ].map((feature, idx) => (
              <motion.div key={idx} whileHover={{ y: -10 }} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-amber-500/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
                <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-amber-500/30 group-hover:border-amber-500/60 transition-all duration-300">
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            variants={floatingVariants}
            animate="animate"
            className="text-6xl absolute top-1/3 left-10 opacity-10"
          >
            ₿
          </motion.div>
          <motion.div
            variants={floatingVariants}
            animate="animate"
            transition={{ duration: 4 }}
            className="text-6xl absolute bottom-1/3 right-10 opacity-10"
          >
            Ξ
          </motion.div>
        </motion.div>
      </div>

      {!loading && (
        <>
          <section id="how-it-works">
            <HowItWorks />
          </section>
          <section id="about">
            <AboutSection content={landingContent.content} />
          </section>
          <TrustSection />
          <TestimonialsSection testimonials={landingContent.testimonials} />
          <section id="faq">
            <FAQSection faqs={landingContent.faqs} />
          </section>
        </>
      )}

      <Footer contactInfo={landingContent.content} />
    </div>
  );
};

export default LandingPage;
